import React, { useEffect, useMemo, useState } from "react";
import { FaLock, FaMinus, FaPlus } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { encryptData } from "../../funcs/encryption";

const shuffleStyle = `
  @keyframes shuffleWiggle {
    0% { transform: translate(0, 0) rotate(0deg); }
    25% { transform: translate(-5px, 5px) rotate(-3deg); }
    50% { transform: translate(5px, -5px) rotate(3deg); }
    75% { transform: translate(-3px, 3px) rotate(-2deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
  }

  .shuffle-animation {
    animation: shuffleWiggle 1s ease-in-out;
  }
`;

/** Shuffle in-place */
const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

/** Sort descending by points (highest→lowest) */
const sortByPoints = (array) => {
  return [...array].sort((a, b) => {
    const aPoints = a.question?.points || a.customQuestion?.points || 0;
    const bPoints = b.question?.points || b.customQuestion?.points || 0;
    // b - a => descending
    return bPoints - aPoints;
  });
};

const MKH = ({ game, setGame, ads }) => {
  const navigate = useNavigate();
  const { partnerSlug } = useParams();
  // Keep the /partner/:slug prefix when navigating within a partner page.
  const partnerBase = partnerSlug ? `/partner/${partnerSlug}` : "";

  const [showPoints, setShowPoints] = useState(true);
  const [isShuffling, setIsShuffling] = useState(false);

  // Identify the game code
  const gameCode = game?.codeInfo?.code || "";
  const shuffledKey = `shuffled_${gameCode}`;
  const fadedKey = `faded_${gameCode}`;

  // Check localStorage flags
  const hasShuffled = !!localStorage.getItem(shuffledKey);
  const hasFaded = !!localStorage.getItem(fadedKey);

  /**
   * Main effect controlling shuffle/fade once per game code.
   */
  useEffect(() => {
    if (!game || !gameCode) return;

    // If not fading questions => show points, no shuffle
    if (!game.fadedQuestions) {
      setShowPoints(true);
      setIsShuffling(false);
      return;
    }

    // If we've already done the fade => keep points hidden
    if (hasFaded) {
      setShowPoints(false);
      setIsShuffling(false);
      return;
    }

    // Otherwise, shuffle once if not done
    if (!hasShuffled) {
      const updatedGame = { ...game };
      updatedGame.modes = updatedGame.modes.map((mode) => {
        if (!mode?.questions) return mode;
        return { ...mode, questions: shuffleArray(mode.questions) };
      });

      // Store new game so parent sees it
      const enc = encryptData(updatedGame);
      localStorage.setItem("game", enc);
      setGame(updatedGame);

      localStorage.setItem(shuffledKey, "true");
    }

    // Now do wiggle => fade
    setShowPoints(true);
    setIsShuffling(true);

    const wiggleTimer = setTimeout(() => {
      setIsShuffling(false);
      const fadeTimer = setTimeout(() => {
        setShowPoints(false);
        localStorage.setItem(fadedKey, "true");
      }, 100);
      return () => clearTimeout(fadeTimer);
    }, 1000);

    return () => clearTimeout(wiggleTimer);
  }, [game, gameCode, setGame, hasShuffled, hasFaded, shuffledKey, fadedKey]);

  /**
   * finalModes:
   *   - If fadedQuestions = false => sort descending
   *   - If we have fully faded => keep as-is
   *   - Otherwise => the effect above sets the updatedGame
   */
  const finalModes = useMemo(() => {
    if (!game?.modes) return [];

    if (!game.fadedQuestions) {
      // Sort descending if not fading
      return game.modes.map((mode) => ({
        ...mode,
        questions: sortByPoints(mode.questions || []),
      }));
    }

    if (hasFaded) {
      // Already faded => keep as is
      return game.modes;
    }

    // Not yet faded => rely on updated shuffled game
    return game.modes;
  }, [game, hasFaded]);

  const isSingleMode = (finalModes?.length || 0) === 1;

  const renderSingleMode = () => {
    const mode = finalModes[0];
    const sortedQs = mode?.questions || [];
    const indexed = sortedQs.map((q, idx) => ({ q, idx }));
    const leftQs = indexed.filter(({ idx }) => idx % 2 === 0);
    const rightQs = indexed.filter(({ idx }) => idx % 2 !== 0);

    const lastUnansweredReversedIndex = [...sortedQs]
      .reverse()
      .findIndex(
        (x) => !(x?.question?.answered || x?.customQuestion?.answered),
      );
    const lastUnansweredIndex =
      lastUnansweredReversedIndex === -1
        ? -1
        : sortedQs.length - 1 - lastUnansweredReversedIndex;

    const renderCard = ({ q, idx }) => {
      const isAnswered = q?.question?.answered || q?.customQuestion?.answered;
      const shouldDisable =
        isAnswered ||
        (game?.lowToHighPoints && !isAnswered && idx !== lastUnansweredIndex);

      return (
        <div key={q._id} className="w-1/3 h-1/6 p-1 relative">
          <button
            onClick={() => navigate(`${partnerBase}/question/${q?._id}`)}
            disabled={shouldDisable}
            className={`
              h-full w-full bg-white rounded-xl text-[#29a6d6] font-bold
              ${shouldDisable ? "opacity-50" : "hover:bg-[#f0f8fc]"}
              ${
                game?.lowToHighPoints && !isAnswered && shouldDisable
                  ? "disabled:bg-[#f5f5f5] disabled:text-gray-400"
                  : "disabled:bg-[#e8e8e8] disabled:text-gray-500"
              }
              ${isShuffling ? "shuffle-animation" : ""}
            `}
          >
            <h1>
              {!showPoints && !isAnswered
                ? idx + 1
                : q?.question?.points || q?.customQuestion?.points}
            </h1>
          </button>
          {game?.lowToHighPoints && !isAnswered && shouldDisable && (
            <div className="absolute inset-0 flex justify-center items-center">
              <FaLock className="text-gray-400 size-4 lg:size-6" />
            </div>
          )}
        </div>
      );
    };

    return (
      <div
        className={`relative h-full w-full rounded-2xl bg-[#bfe4f3] p-2 flex flex-row gap-2 ${
          mode?.custom ? "border-4 border-mainText" : ""
        }`}
      >
        <div className="w-[40%] h-full flex flex-row flex-wrap content-center">
          {leftQs.map(renderCard)}
        </div>
        <div className="w-[30%] h-full relative bg-[#eaf6fb] rounded-xl overflow-hidden flex flex-col items-center justify-center">
          <div className="w-[80%] aspect-square border-4 border-gray-600 rounded-full overflow-hidden mb-2">
            <img
              src={mode?.image}
              alt={mode?.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="px-3 py-2 bg-[#94d2ea] rounded-full">
            <h1 className="text-sm sm:text-sm lg:text-lg text-center">
              {mode?.name}
            </h1>
          </div>
          {mode?.custom && (
            <div className="absolute w-full flex justify-center items-center top-[-3%] z-50">
              <div className="text-xs lg:text-sm rounded-xl p-2 bg-[#29a6d6] text-white">
                <h1>فئة خاصة</h1>
              </div>
            </div>
          )}
        </div>
        <div className="w-[40%] h-full flex flex-row flex-wrap content-center">
          {rightQs.map(renderCard)}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-[91vh]">
      <style>{shuffleStyle}</style>
      <div className="w-full h-full p-2 md:p-6 flex flex-col sm:flex-row">
        <div className="h-5/6 sm:h-full w-full sm:w-5/6 flex flex-row flex-wrap text-base sm:text-lg lg:text-2xl font-bold justify-end ">
          {isSingleMode
            ? renderSingleMode()
            : finalModes?.map((mode, modeIndex) => (
                <div key={modeIndex} className="w-2/6 sm:h-full sm:w-1/6 pr-1">
                  <div
                    className={`relative h-full w-full rounded-2xl bg-[#bfe4f3] p-1 ${
                      mode?.custom && "border-4 border-mainText"
                    }`}
                  >
                    <div className="relative h-1/6 bg-[#eaf6fb] rounded-xl overflow-hidden flex justify-center items-center">
                      <div className="w-[70%] sm:min-w-32 sm:w-[96%] aspect-square border-4 border-gray-600 rounded-full overflow-hidden opacity-35 absolute top-0 z-0">
                        <img
                          src={mode?.image}
                          alt={mode?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="opacity-100 z-10 px:1 p-1 sm:px-3 sm:p-2 bg-[#94d2ea] rounded-full">
                        <h1 className="text-sm sm:text-sm lg:text-lg text-center">
                          {mode?.name}
                        </h1>
                      </div>
                    </div>
                    <div className="h-5/6">
                      {mode?.questions?.map((q, qIndex, arr) => {
                        const isAnswered =
                          q?.question?.answered || q?.customQuestion?.answered;

                        let shouldDisable = isAnswered;
                        if (game?.lowToHighPoints && !isAnswered) {
                          // Instead of "firstUnansweredIndex", we find the "last" unanswered
                          // which is effectively the lowest points after descending sort
                          const lastUnansweredReversedIndex = [...arr]
                            .reverse()
                            .findIndex(
                              (x) =>
                                !(
                                  x?.question?.answered ||
                                  x?.customQuestion?.answered
                                ),
                            );
                          // convert reversed index to normal index
                          const lastUnansweredIndex =
                            lastUnansweredReversedIndex === -1
                              ? -1
                              : arr.length - 1 - lastUnansweredReversedIndex;

                          // disable if not the last unanswered
                          shouldDisable = qIndex !== lastUnansweredIndex;
                        }

                        return (
                          <div key={q._id} className="h-1/6 pt-1 px-1 relative">
                            <button
                              onClick={() => navigate(`${partnerBase}/question/${q?._id}`)}
                              disabled={shouldDisable}
                              className={`
                            h-full w-full bg-white rounded-xl text-[#29a6d6] font-bold
                            ${
                              shouldDisable
                                ? "opacity-50"
                                : "hover:bg-[#f0f8fc]"
                            }
                            ${
                              game?.lowToHighPoints &&
                              !isAnswered &&
                              shouldDisable
                                ? "disabled:bg-[#f5f5f5] disabled:text-gray-400"
                                : "disabled:bg-[#e8e8e8] disabled:text-gray-500"
                            }
                            ${isShuffling ? "shuffle-animation" : ""}
                          `}
                            >
                              <h1>
                                {!showPoints && !isAnswered
                                  ? qIndex + 1
                                  : q?.question?.points || q?.customQuestion?.points}
                              </h1>
                            </button>

                            {/* Lock icon if disabled but unanswered (lowToHighPoints) */}
                            {game?.lowToHighPoints &&
                              !isAnswered &&
                              shouldDisable && (
                                <div className="absolute inset-0 flex justify-center items-center">
                                  <FaLock className="text-gray-400 size-4 lg:size-6" />
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>
                    {mode?.custom && (
                      <div className="absolute w-full flex justify-center items-center top-[-3%] z-50">
                        <div className="text-xs lg:text-sm rounded-xl p-2 bg-[#29a6d6] text-white">
                          <h1>فئة خاصة</h1>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
        </div>
        <div className="h-1/6 sm:h-full w-full sm:w-1/6 py-2 sm:py-0">
          {/* Teams Sidebar - MKH Question.js style */}
          <div className="bg-white rounded-xl p-3 shadow-lg border-2 border-[#bfe4f3] h-full flex flex-col">
            <h3 className="text-[#29a6d6] font-bold mb-3 text-center text-sm flex-shrink-0">
              الفرق
            </h3>
            <div className="flex-1 flex flex-col h-full">
              {/* Teams Section */}
              <div className="flex flex-col gap-2 h-full">
                {game?.teams?.map((team, index) => (
                  <div
                    key={index}
                    className={`bg-[#eaf6fb] rounded-lg p-2 border-2 flex-shrink-0 h-[24%] flex flex-col justify-between items-center ${
                      team.currentTurn ? "border-[#29a6d6]" : "border-[#bfe4f3]"
                    }`}
                  >
                    <div className="flex  items-center justify-between h-1/2">
                      <div className="flex items-center space-x-2 w-full justify-center">
                        <div className=" w-1/3 rounded-full overflow-hidden border border-[#bfe4f3]">
                          <img
                            src={team?.image?.image}
                            alt={team?.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h4 className="font-bold text-[#29a6d6] text-xs">
                            {team.name}
                          </h4>
                        </div>
                      </div>
                    </div>

                    {/* Point adjustment buttons */}
                    <div className="flex items-center space-x-1 justify-center h-1/2">
                      <button
                        onClick={() => {
                          const updatedGame = { ...game };
                          updatedGame.teams[index].points -= 50;
                          setGame(updatedGame);
                          const encrypted = encryptData(updatedGame);
                          localStorage.setItem("game", encrypted);
                        }}
                        className="p-1 rounded text-s transition-colors bg-[#bfe4f3] text-[#29a6d6] hover:bg-[#a8d4e8]"
                      >
                        <FaMinus size={10} />
                      </button>
                      <span className="font-bold text-xs min-w-[30px] text-center text-[#29a6d6]">
                        {team.points}
                      </span>
                      <button
                        onClick={() => {
                          const updatedGame = { ...game };
                          updatedGame.teams[index].points += 50;
                          setGame(updatedGame);
                          const encrypted = encryptData(updatedGame);
                          localStorage.setItem("game", encrypted);
                        }}
                        className="p-1 rounded text-s transition-colors bg-[#bfe4f3] text-[#29a6d6] hover:bg-[#a8d4e8]"
                      >
                        <FaPlus size={10} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Ads Section - Takes remaining space */}
              {ads && Array.isArray(ads) && ads.length > 0 && (
                <div className="flex-1 min-h-0 mt-2">
                  <h4 className="text-[#29a6d6] font-bold mb-2 text-center text-xs">
                    الإعلانات
                  </h4>
                  <div className="space-y-1 h-full overflow-hidden">
                    {ads.slice(0, 4).map((ad, index) => (
                      <div
                        key={index}
                        className="rounded-lg overflow-hidden border border-[#bfe4f3]"
                        style={{
                          height: `calc((100% - ${
                            (Math.min(ads.length, 4) - 1) * 0.25
                          }rem) / ${Math.min(ads.length, 4)})`,
                        }}
                      >
                        <img
                          src={ad.image}
                          alt={ad.name}
                          className="w-full h-3/4 object-cover"
                        />
                        <div className="p-1 bg-[#eaf6fb] h-1/4 flex items-center justify-center">
                          <p className="text-[#29a6d6] text-xs font-semibold text-center truncate">
                            {ad.name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MKH;
